import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/shared/components/ui/form";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { usePutAirSamplerMutation } from "@/services/backend/airsamplers/service";
import { AirSampler } from "@/services/backend/airsamplers/air-sampler";
import { Pencil } from "lucide-react";
import { useEffect, useState } from "react";
import { RTKQueryErrorAlert } from "@/shared/components/domain/errors/rtk-query-error-alert";

const formSchema = z.object({
  description: z.string(),
  holeCount: z.string(),
});

interface EditAirSamplerDialogProps {
  airSampler: AirSampler;
}

export default function EditAirSamplerDialog({
  airSampler,
}: EditAirSamplerDialogProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [putSampler, { isLoading, isSuccess, error }] =
    usePutAirSamplerMutation();
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      description: airSampler.description,
      holeCount: airSampler.holeCount.toString(),
    },
  });

  useEffect(() => {
    function reset(): void {
      if (isSuccess) {
        form.reset();
        setIsOpen(false);
      }
    }

    reset();
  }, [form, isSuccess]);

  function onSubmit(values: z.infer<typeof formSchema>) {
    putSampler({
      airSampler: {
        id: airSampler.id,
        originalId: airSampler.originalId,
        description: values.description,
        holeCount: Number(values.holeCount),
      },
    });
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="outline">
          <Pencil />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Neuen Luftkeimsammler anlegen</DialogTitle>
        </DialogHeader>
        <RTKQueryErrorAlert error={error} />
        <div className="flex items-center space-x-2">
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="w-full space-y-4"
            >
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Beschreibung</FormLabel>
                    <FormControl>
                      <Input
                        disabled={isLoading}
                        placeholder="Beschreibung"
                        type="text"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      Beschreibung des Luftkeimsammlers (z.B. Name)
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="holeCount"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Lochzahl</FormLabel>
                    <FormControl>
                      <Input
                        disabled={isLoading}
                        placeholder="Lochzahl"
                        type="number"
                        min={0}
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      Lochanzahl des Luftkeimsammlers
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button disabled={isLoading} type="submit">
                Submit
              </Button>
            </form>
          </Form>{" "}
        </div>
      </DialogContent>
    </Dialog>
  );
}
